'use client'

import { type AxiosError } from 'axios'
import { getCookie } from 'cookies-next/client'
import { useCallback } from 'react'
import { type SWRResponse } from 'swr'
import useSWRImmutable from 'swr/immutable'

import { type CrossSellResponse } from '@redteclab/api/clients/retail-media-xsell-service'

import { adServerCookiesGetOrGenerateUserIdentity } from '../../../ad-server'
import { buildHomeOneApiAxiosInstance } from '../../../base-api-communication'
import {
  COOKIE_AD_SERVER_USER_ID_NAME,
  COOKIE_ECONDA_VISITOR_ID_NAME,
} from '../../../cookies'
import { useExpaContext } from '../../../expa'
import { useExperimentsContext } from '../../../experiments/components/ExperimentsContext'
import { experimentAccessorIsAdServerEnabled } from '../../../experiments/model/experimentAccessor'
import { useGlobalConfigContext } from '../../../global-config'
import { isServerSide } from '../../../request-context'
import { urlResolverGetCrossSellHorizontalSliderApiEndpoint } from '../../../url-handling'

export type CrossSellHorizontalSliderRequestParams = {
  categoryId?: string
  categoryLabel?: string
  cmUid?: string
  emcid?: string
  listPrice?: string
  productId?: string
  retailPrice?: string
  sellerId?: string
  visibleItemCount?: number
  widgetId?: number
}

const extractEmvidFromVisitorIdCookie = (
  visitorIdCookie: string | undefined,
): string | null => {
  if (!visitorIdCookie) {
    return null
  }

  const parts = visitorIdCookie.split(':')

  return parts.at(0) ?? null
}

const CROSS_SELL_HOMEPAGE_TOP_COM_DE_ID = 294
const CROSS_SELL_HOMEPAGE_TOP_AT_DE_ID = 351
const CROSS_SELL_HOMEPAGE_TOP_IT_IT_ID = 537
const CROSS_SELL_HOMEPAGE_TOP_CH_DE_FR_ID = 408
const CROSS_SELL_HOMEPAGE_TOP_BE_FR_NL_ID = 489
const CROSS_SELL_HOMEPAGE_TOP_FR_FR_ID = 74

const CROSS_SELL_HOMEPAGE_BOTTOM_COM_DE_ID = 299
const CROSS_SELL_HOMEPAGE_BOTTOM_AT_DE_ID = 376
const CROSS_SELL_HOMEPAGE_BOTTOM_IT_IT_ID = 544
const CROSS_SELL_HOMEPAGE_BOTTOM_CH_FR_DE_ID = 430
const CROSS_SELL_HOMEPAGE_BOTTOM_FR_FR_ID = 97
const CROSS_SELL_HOMEPAGE_BOTTOM_BE_FR_NL_ID = 511

const CROSS_SELL_CATEGORY_BE_FR_NL_ID = 490

const CROSS_SELL_LAYOUT_COM_DE_ID = 329
const CROSS_SELL_LAYOUT_AT_DE_ID = 401
const CROSS_SELL_LAYOUT_CH_DE_FR_ID = 535
const CROSS_SELL_LAYOUT_IT_IT_ID = 573
const CROSS_SELL_LAYOUT_BE_FR_NL_ID = 526

const CROSS_SELL_PDP_CENTER_SLIDER_COM_DE_ID = 297
const CROSS_SELL_PDP_CENTER_SLIDER_AT_DE_ID = 356
const CROSS_SELL_PDP_CENTER_SLIDER_CH_DE_FR_ID = 413
const CROSS_SELL_PDP_CENTER_SLIDER_IT_IT_ID = 542
const CROSS_SELL_PDP_CENTER_SLIDER_BE_FR_NL_ID = 492

const CROSS_SELL_ADD_TO_CART_TOP_COM_DE_ID = 317
const CROSS_SELL_ADD_TO_CART_TOP_AT_DE_ID = 359
const CROSS_SELL_ADD_TO_CART_TOP_IT_IT_ID = 545
const CROSS_SELL_ADD_TO_CART_TOP_CH_FR_DE_ID = 416
const CROSS_SELL_ADD_TO_CART_TOP_FR_FR_ID = 82
const CROSS_SELL_ADD_TO_CART_TOP_BE_FR_NL_ID = 497

/*
 * For this specific widget ID, data needs to be fetched from the Retail Media API.
 * See ticket RMADT-2349 for more details.
 */
const crossSellWidgetsWithAds = new Set([
  CROSS_SELL_ADD_TO_CART_TOP_AT_DE_ID,
  CROSS_SELL_ADD_TO_CART_TOP_BE_FR_NL_ID,
  CROSS_SELL_ADD_TO_CART_TOP_CH_FR_DE_ID,
  CROSS_SELL_ADD_TO_CART_TOP_COM_DE_ID,
  CROSS_SELL_ADD_TO_CART_TOP_FR_FR_ID,
  CROSS_SELL_ADD_TO_CART_TOP_IT_IT_ID,
  CROSS_SELL_CATEGORY_BE_FR_NL_ID,
  CROSS_SELL_HOMEPAGE_BOTTOM_AT_DE_ID,
  CROSS_SELL_HOMEPAGE_BOTTOM_BE_FR_NL_ID,
  CROSS_SELL_HOMEPAGE_BOTTOM_CH_FR_DE_ID,
  CROSS_SELL_HOMEPAGE_BOTTOM_COM_DE_ID,
  CROSS_SELL_HOMEPAGE_BOTTOM_FR_FR_ID,
  CROSS_SELL_HOMEPAGE_BOTTOM_IT_IT_ID,
  CROSS_SELL_HOMEPAGE_TOP_AT_DE_ID,
  CROSS_SELL_HOMEPAGE_TOP_BE_FR_NL_ID,
  CROSS_SELL_HOMEPAGE_TOP_CH_DE_FR_ID,
  CROSS_SELL_HOMEPAGE_TOP_COM_DE_ID,
  CROSS_SELL_HOMEPAGE_TOP_FR_FR_ID,
  CROSS_SELL_HOMEPAGE_TOP_IT_IT_ID,
  CROSS_SELL_LAYOUT_AT_DE_ID,
  CROSS_SELL_LAYOUT_BE_FR_NL_ID,
  CROSS_SELL_LAYOUT_CH_DE_FR_ID,
  CROSS_SELL_LAYOUT_COM_DE_ID,
  CROSS_SELL_LAYOUT_IT_IT_ID,
  CROSS_SELL_PDP_CENTER_SLIDER_AT_DE_ID,
  CROSS_SELL_PDP_CENTER_SLIDER_BE_FR_NL_ID,
  CROSS_SELL_PDP_CENTER_SLIDER_CH_DE_FR_ID,
  CROSS_SELL_PDP_CENTER_SLIDER_COM_DE_ID,
  CROSS_SELL_PDP_CENTER_SLIDER_IT_IT_ID,
])

export const useSwrCrossSellHorizontalSlider = (
  params?: CrossSellHorizontalSliderRequestParams,
): SWRResponse<CrossSellResponse, AxiosError> => {
  const publicGlobalConfig = useGlobalConfigContext()
  const { language, locale, shopEnv, tenant, tenantAndEnv } = publicGlobalConfig
  const { experiments } = useExperimentsContext()
  const isAdServerEnabled = experimentAccessorIsAdServerEnabled(experiments)

  const expa = useExpaContext() ?? 'direct'

  let crossSellParams: CrossSellHorizontalSliderRequestParams | null = null

  if (!isServerSide()) {
    crossSellParams = {
      cmUid: params?.cmUid,
      // @ts-expect-error -- need update
      coreShopEnvironment: shopEnv,
      csc: expa,
      csize: 50,
      'ctxcat.cid': params?.categoryId,
      'ctxcat.ct': params?.categoryId && 'productcategory',
      'ctxcustom.content': expa,
      'ctxcustom.sellerId': params?.sellerId,
      emcid: params?.emcid,
      expa,
      language,
      listPrice: params?.listPrice,
      locale,
      pid: params?.productId,
      retailPrice: params?.retailPrice,
      start: 0,
      tenant,
      tenantPath: tenantAndEnv,
      type: 'cs',
      visibleItemCount: params?.visibleItemCount,
      wid: params?.widgetId,
      widgetdetails: true,
    }
  }

  const fetcher = useCallback(
    async ([url, wid, fetchParams]: [
      url: string,
      wid: CrossSellHorizontalSliderRequestParams['widgetId'],
      fetchParams: CrossSellHorizontalSliderRequestParams,
    ]): Promise<CrossSellResponse> => {
      const emvid = extractEmvidFromVisitorIdCookie(
        getCookie(COOKIE_ECONDA_VISITOR_ID_NAME),
      )

      if (isAdServerEnabled && crossSellWidgetsWithAds.has(Number(wid))) {
        const { browserIdConversion, browserIdTargeting } =
          adServerCookiesGetOrGenerateUserIdentity()
        const body = {
          adServerTargeting: window._targeting,
          user: {
            asbidc: browserIdConversion,
            asbidt: browserIdTargeting,
            asuid: getCookie(COOKIE_AD_SERVER_USER_ID_NAME),
          },
        }

        return buildHomeOneApiAxiosInstance(publicGlobalConfig)
          .post<CrossSellResponse>(url, body, {
            params: { ...fetchParams, emvid },
          })
          .then(({ data }) => data)
      }

      return buildHomeOneApiAxiosInstance(publicGlobalConfig)
        .get<CrossSellResponse>(url, { params: { ...fetchParams, emvid } })
        .then(({ data }) => data)
    },
    [publicGlobalConfig, isAdServerEnabled],
  )

  return useSWRImmutable(() => {
    if (!crossSellParams || !params) {
      return null
    }

    const { widgetId } = params

    return [
      urlResolverGetCrossSellHorizontalSliderApiEndpoint(),
      widgetId,
      crossSellParams,
    ]
  }, fetcher)
}
